import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Edit3, Save, X, MapPin, Globe, Linkedin,
  Twitter, Phone, Building2, Star, FileText, Users,
  Briefcase, BarChart3, Plus, Loader2, Trash2, Check, Rss,
  TrendingUp, Activity, CheckCircle
} from "lucide-react";
import { StarRating } from "./ReputationBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  investor: "Real Estate Investor",
  lender: "Private Lender",
  realtor: "Realtor / Agent",
  contractor: "General Contractor",
  title: "Title Company",
};

const ROLE_COLORS = {
  investor: "#D4AF37",
  lender: "#0A84FF",
  realtor: "#00FF88",
  contractor: "#FF9F0A",
  title: "#BF5AF2",
};

function Avatar({ url, name, size = 96, editable, onUpload, uploading }) {
  const ref = useRef();
  const initials = (name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <div
        className="rounded-full overflow-hidden border-4 border-[#050505] bg-[#1A1A1A]"
        style={{ width: size, height: size }}
      >
        {url ? (
          <img src={`${BACKEND_URL}${url}`} alt="avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#D4AF37] font-bold"
            style={{ fontSize: size * 0.35 }}>
            {initials}
          </div>
        )}
      </div>
      {editable && (
        <>
          <button
            onClick={() => ref.current?.click()}
            disabled={uploading}
            className="absolute bottom-0 right-0 w-8 h-8 bg-[#D4AF37] hover:bg-[#C4A030] rounded-full flex items-center justify-center shadow-lg transition-all"
            data-testid="upload-avatar-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 text-black animate-spin" /> : <Camera className="w-3.5 h-3.5 text-black" />}
          </button>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onUpload} />
        </>
      )}
    </div>
  );
}

function StatPill({ icon: Icon, label, value, color = "text-white/60" }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-4 py-2">
      <p className={`font-bold text-lg ${color}`}>{value}</p>
      <div className={`flex items-center gap-1 text-[10px] text-white/30`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
    </div>
  );
}

function SocialLink({ href, icon: Icon, label, color }) {
  if (!href) return null;
  const url = href.startsWith("http") ? href : `https://${href}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors text-xs">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span>{label || href}</span>
    </a>
  );
}

export default function NetworkProfileTab({ user }) {
  const [profile, setProfile] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [posts, setPosts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [form, setForm] = useState({});
  const bannerRef = useRef();

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [profRes, repRes, postsRes, dealsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reputation/my`, { withCredentials: true }).catch(() => ({ data: { total_reviews: 0, average_rating: 0 } })),
        axios.get(`${BACKEND_URL}/api/network/posts?limit=6`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProfile(profRes.data);
      setReputation(repRes.data);
      const allPosts = Array.isArray(postsRes.data) ? postsRes.data : postsRes.data?.posts || [];
      setPosts(allPosts.filter(p =>
        p.author_id === user?.user_id ||
        p.user_id   === user?.user_id
      ).slice(0, 6));
      setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : []);
      setForm({
        display_name: profRes.data.display_name || "",
        bio: profRes.data.bio || "",
        company: profRes.data.company || "",
        role_title: profRes.data.role_title || "",
        location: profRes.data.location || "",
        website: profRes.data.website || "",
        linkedin: profRes.data.linkedin || "",
        twitter: profRes.data.twitter || "",
        phone: profRes.data.phone || "",
      });
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, form, { withCredentials: true });
      setProfile(p => ({ ...p, ...form }));
      setEditing(false);
      toast.success("Profile saved!");
    } catch (err) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2MB for avatar"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${BACKEND_URL}/api/profile/picture`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProfile(p => ({ ...p, profile_picture_url: res.data.url }));
      toast.success("Avatar updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB for banner"); return; }
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${BACKEND_URL}/api/profile/banner`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProfile(p => ({ ...p, banner_url: res.data.url }));
      toast.success("Banner updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeBanner = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/profile/banner`, { withCredentials: true });
      setProfile(p => ({ ...p, banner_url: null }));
      toast.success("Banner removed");
    } catch {
      toast.error("Failed to remove banner");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const displayName = profile?.display_name || profile?.name || user?.name || "Your Name";
  const roleLabel = ROLE_LABELS[profile?.primary_role] || profile?.role_title || "Real Estate Professional";
  const roleColor = ROLE_COLORS[profile?.primary_role] || "#D4AF37";
  const memberYear = profile?.created_at ? new Date(profile.created_at).getFullYear() : new Date().getFullYear();

  return (
    <div className="space-y-0 -mx-6 -mt-6" data-testid="network-profile-tab">
      {/* ── Banner ─────────────────────────────────────────────────────────── */}
      <div className="relative h-52 md:h-64 overflow-hidden group">
        {profile?.banner_url ? (
          <img
            src={`${BACKEND_URL}${profile.banner_url}`}
            alt="Profile banner"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A1209] via-[#0D0D0D] to-[#0A0A0A]">
            {/* Subtle geometric pattern */}
            <div className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0, #D4AF37 1px, transparent 0, transparent 50%)",
                backgroundSize: "24px 24px"
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-[#D4AF37]/20 font-bold text-5xl tracking-[0.3em] uppercase select-none">DEALFORGE</p>
            </div>
          </div>
        )}

        {/* Banner overlay controls */}
        <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => bannerRef.current?.click()} disabled={uploadingBanner}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-white/20 text-white text-xs rounded-lg hover:bg-black/80 transition-all"
            data-testid="upload-banner-btn">
            {uploadingBanner ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            {profile?.banner_url ? "Change" : "Upload"} Banner
          </button>
          {profile?.banner_url && (
            <button onClick={removeBanner}
              className="flex items-center gap-1 px-2 py-1.5 bg-black/60 backdrop-blur-sm border border-red-500/30 text-red-400 text-xs rounded-lg hover:bg-red-500/10 transition-all">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          <input ref={bannerRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadBanner} />
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-[#050505] to-transparent" />
      </div>

      {/* ── Identity Row ──────────────────────────────────────────────────── */}
      <div className="px-6 -mt-14 relative z-10 flex items-end justify-between gap-4 mb-4">
        <Avatar
          url={profile?.profile_picture_url}
          name={displayName}
          size={88}
          editable
          onUpload={uploadAvatar}
          uploading={uploadingAvatar}
        />
        <div className="flex items-center gap-2 pb-1">
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 border border-white/[0.08] text-white/50 hover:text-white rounded-lg text-xs transition-all">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                data-testid="save-profile-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold rounded-lg text-xs transition-all disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save Profile
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)}
              data-testid="edit-profile-btn"
              className="flex items-center gap-1.5 px-4 py-1.5 border border-white/[0.12] text-white/60 hover:text-white hover:border-white/30 rounded-lg text-xs transition-all">
              <Edit3 className="w-3.5 h-3.5" /> Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="px-6 space-y-4">
        {/* ── Name & Role ─────────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-white font-bold text-2xl">{displayName}</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider"
              style={{ color: roleColor, borderColor: `${roleColor}40`, background: `${roleColor}12` }}>
              {roleLabel}
            </span>
          </div>
          {profile?.company && (
            <p className="text-white/40 text-sm mt-0.5 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> {profile.company}
            </p>
          )}
        </div>

        {/* ── Bio ─────────────────────────────────────────────────────────── */}
        {profile?.bio && !editing && (
          <p className="text-white/60 text-sm leading-relaxed max-w-2xl">{profile.bio}</p>
        )}

        {/* ── Social Links ────────────────────────────────────────────────── */}
        {!editing && (
          <div className="flex flex-wrap items-center gap-2">
            {profile?.location && (
              <span className="flex items-center gap-1.5 text-white/50 text-xs bg-white/[0.04] border border-white/[0.06] px-3 py-1.5 rounded-full">
                <MapPin className="w-3 h-3 text-[#D4AF37]" /> {profile.location}
              </span>
            )}
            {profile?.website && (
              <a href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs bg-white/[0.04] border border-white/[0.06] hover:border-[#0A84FF]/30 px-3 py-1.5 rounded-full transition-all">
                <Globe className="w-3 h-3 text-[#0A84FF]" />
                {profile.website.replace(/^https?:\/\//, "").split("/")[0]}
              </a>
            )}
            {profile?.linkedin && (
              <a href={profile.linkedin.startsWith("http") ? profile.linkedin : `https://${profile.linkedin}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs bg-white/[0.04] border border-white/[0.06] hover:border-[#0A84FF]/30 px-3 py-1.5 rounded-full transition-all">
                <Linkedin className="w-3 h-3 text-[#0A84FF]" /> LinkedIn
              </a>
            )}
            {profile?.twitter && (
              <a href={profile.twitter.startsWith("http") ? profile.twitter : `https://twitter.com/${profile.twitter.replace("@", "")}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs bg-white/[0.04] border border-white/[0.06] hover:border-[#1DA1F2]/30 px-3 py-1.5 rounded-full transition-all">
                <Twitter className="w-3 h-3 text-[#1DA1F2]" /> Twitter
              </a>
            )}
            {profile?.phone && (
              <span className="flex items-center gap-1.5 text-white/50 text-xs bg-white/[0.04] border border-white/[0.06] px-3 py-1.5 rounded-full">
                <Phone className="w-3 h-3" /> {profile.phone}
              </span>
            )}
            <span className="flex items-center gap-1.5 text-white/30 text-xs bg-white/[0.02] border border-white/[0.04] px-3 py-1.5 rounded-full">
              <Star className="w-3 h-3 text-[#D4AF37]/50" /> Since {memberYear}
            </span>
          </div>
        )}

        {/* ── Stats Grid ──────────────────────────────────────────────────── */}
        {!editing && (
          <div className="space-y-3">
            {/* Top stats row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/10 rounded-xl p-4 text-center hover:border-[#D4AF37]/20 transition-all">
                <p className="text-2xl font-bold text-[#D4AF37]">{profile?.deals_count ?? deals.length ?? 0}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <FileText className="w-3 h-3 text-white/20" />
                  <p className="text-[11px] text-white/30">Total Deals</p>
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 text-center hover:border-white/[0.12] transition-all">
                <p className="text-2xl font-bold text-white">{profile?.posts_count ?? posts.length}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <BarChart3 className="w-3 h-3 text-white/20" />
                  <p className="text-[11px] text-white/30">Posts</p>
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-emerald-500/10 rounded-xl p-4 text-center hover:border-emerald-500/20 transition-all">
                <p className="text-2xl font-bold text-emerald-400">
                  {deals.filter(d => d.deal_status === "closed" || d.pipeline_stage === "closed").length}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Briefcase className="w-3 h-3 text-white/20" />
                  <p className="text-[11px] text-white/30">Closed</p>
                </div>
              </div>
            </div>

            {/* Credibility row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/10 rounded-xl p-4 hover:border-[#D4AF37]/20 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">Total Deal Volume</p>
                </div>
                <p className="text-lg font-bold text-[#D4AF37]">
                  {(() => {
                    const total = deals.reduce((sum, d) => sum + (d.arv || d.purchase_price || 0), 0);
                    if (total >= 1000000) return `$${(total/1000000).toFixed(1)}M`;
                    if (total >= 1000) return `$${(total/1000).toFixed(0)}K`;
                    return total > 0 ? `$${total.toLocaleString()}` : "—";
                  })()}
                </p>
                <p className="text-[10px] text-white/20 mt-0.5">Across {deals.length} deals</p>
              </div>
              <div className="bg-[#0A0A0A] border border-blue-500/10 rounded-xl p-4 hover:border-blue-500/20 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-3.5 h-3.5 text-blue-400" />
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">Active Now</p>
                </div>
                <p className="text-lg font-bold text-blue-400">
                  {deals.filter(d => ["live","under_contract","funded","in_rehab"].includes(d.deal_status)).length}
                </p>
                <p className="text-[10px] text-white/20 mt-0.5">Active deals</p>
              </div>
            </div>

            {/* Verified + Rating */}
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    profile?.has_founder_badge ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/30' : 'bg-white/[0.04] border border-white/[0.08]'
                  }`}>
                    <CheckCircle className={`w-4 h-4 ${profile?.has_founder_badge ? 'text-[#D4AF37]' : 'text-white/20'}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${profile?.has_founder_badge ? 'text-[#D4AF37]' : 'text-white/40'}`}>
                      {profile?.has_founder_badge ? "Verified Investor" : "Not Verified"}
                    </p>
                    <p className="text-[10px] text-white/25">
                      {profile?.has_founder_badge ? "Beta Founder · Identity Confirmed" : "Complete profile to get verified"}
                    </p>
                  </div>
                </div>
                {reputation && reputation.total_reviews > 0 && (
                  <div className="flex items-center gap-2 text-right">
                    <div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= Math.round(reputation.average_rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/10'}`} />
                        ))}
                      </div>
                      <p className="text-[10px] text-white/30 mt-0.5">{reputation.total_reviews} reviews</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Form ───────────────────────────────────────────────────── */}
        {editing && (
          <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-xl p-5 space-y-4" data-testid="profile-edit-form">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold border-b border-white/[0.06] pb-3">Edit Profile</p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs">Display Name</label>
                <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                  placeholder={profile?.name || "Your name"} maxLength={100}
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="input-display-name" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs">Role / Title</label>
                <input value={form.role_title} onChange={e => setForm(f => ({ ...f, role_title: e.target.value }))}
                  placeholder={ROLE_LABELS[profile?.primary_role] || "e.g. Real Estate Investor"} maxLength={100}
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs">Company / Organization</label>
                <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Smith Real Estate LLC" maxLength={100}
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs">Location</label>
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Dallas, TX" maxLength={100}
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <label className="text-white/50 text-xs">Bio <span className="text-white/20">({form.bio?.length || 0}/500)</span></label>
                <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value.slice(0, 500) }))}
                  placeholder="Tell the network about yourself, your specialty, and deal criteria..."
                  rows={3}
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                  data-testid="input-bio" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs flex items-center gap-1.5"><Globe className="w-3 h-3" /> Website</label>
                <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                  placeholder="https://yoursite.com" type="url"
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000" type="tel"
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs flex items-center gap-1.5"><Linkedin className="w-3 h-3 text-[#0A84FF]" /> LinkedIn</label>
                <input value={form.linkedin} onChange={e => setForm(f => ({ ...f, linkedin: e.target.value }))}
                  placeholder="linkedin.com/in/yourname"
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-xs flex items-center gap-1.5"><Twitter className="w-3 h-3 text-[#1DA1F2]" /> Twitter / X</label>
                <input value={form.twitter} onChange={e => setForm(f => ({ ...f, twitter: e.target.value }))}
                  placeholder="@yourtwitterhandle"
                  className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditing(false)} className="flex-1 py-2.5 border border-white/[0.08] text-white/50 hover:text-white rounded-lg text-sm transition-all">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                data-testid="save-profile-form-btn"
                className="flex-1 py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] disabled:opacity-50 text-black font-bold rounded-lg text-sm transition-all flex items-center justify-center gap-2">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Profile</>}
              </button>
            </div>
          </div>
        )}

        {/* ── My Activity Feed ────────────────────────────────────────────── */}
        {!editing && (
          <div className="space-y-4 pb-8">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">My Posts</p>
              {posts.length > 0 && (
                <a href="/network?tab=feed" className="text-[10px] text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors">View Feed →</a>
              )}
            </div>
            {posts.length === 0 ? (
              <div className="text-center py-10 bg-[#0A0A0A] border border-dashed border-white/[0.06] rounded-xl">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/[0.05] border border-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                  <Plus className="w-6 h-6 text-[#D4AF37]/30" />
                </div>
                <p className="text-white/40 text-sm font-medium">No posts yet</p>
                <p className="text-white/20 text-xs mt-1">Share a deal or insight with your network</p>
                <a href="/network?tab=feed"
                  className="inline-flex items-center gap-1.5 mt-4 text-xs text-[#D4AF37] border border-[#D4AF37]/20 px-4 py-2 rounded-lg hover:bg-[#D4AF37]/[0.06] transition-all">
                  <Rss className="w-3 h-3" /> Go to Feed
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <div key={post.post_id || post._id}
                    className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.1] transition-all">
                    <p className="text-white/70 text-sm leading-relaxed line-clamp-3">{post.content}</p>
                    <p className="text-white/20 text-[10px] mt-2">
                      {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
